import { XIcon } from '@phosphor-icons/react'
import * as styles from './active-filter-chips.css.ts'
import { CLASSIFICATION_LABELS, FIX_RESULT_LABELS, PATCH_STATUS_LABELS } from './filter-labels.ts'
import { userSelectedFilterKeys, type FilterStateStore, type FilterStateStoreValues } from './filter-state-store.tsx'

/** The slice of the filter store the chips read and write — the three dimensions they cover. */
export type ActiveFilterChipSource = Pick<
  FilterStateStore,
  'classification' | 'fixResult' | 'patchStatus' | 'toggleClassification' | 'toggleFixResult' | 'togglePatchStatus'
>

export type ActiveFilterChip = {
  id: string
  label: string
  /**
   * Names the dimension as well as the value. "Blocked" and "Excluded" exist in both the
   * classification and fix-result vocabularies, so the visible label alone would give two chips the
   * same accessible name.
   */
  dimensionLabel: string
  onRemove: () => unknown
}

/**
 * One chip per active filter value, rather than one chip per bucket. Selecting an aggregate in the
 * Resolution panel turns on several granular outcomes at once, and the design calls for each to be
 * removable on its own.
 *
 * Scoped to the three dimensions the panel drives. Severity, fix confidence, SCA classification and
 * the rest are set from the Filter dropdown and are counted by `getAdditionalFilterCount`, but get
 * no chip yet.
 */
export const toActiveFilterChips = ({
  filterState,
  defaultFilterState,
}: {
  filterState: ActiveFilterChipSource
  defaultFilterState: FilterStateStoreValues
}): ActiveFilterChip[] => [
  ...userSelectedFilterKeys(filterState.classification, defaultFilterState.classification).map(key => ({
    id: `classification-${key}`,
    label: CLASSIFICATION_LABELS[key],
    dimensionLabel: `${CLASSIFICATION_LABELS[key]} classification`,
    onRemove: () => filterState.toggleClassification(key),
  })),
  ...userSelectedFilterKeys(filterState.fixResult, defaultFilterState.fixResult).map(key => ({
    id: `fixResult-${key}`,
    label: FIX_RESULT_LABELS[key],
    dimensionLabel: `${FIX_RESULT_LABELS[key]} fix result`,
    onRemove: () => filterState.toggleFixResult(key),
  })),
  ...userSelectedFilterKeys(filterState.patchStatus, defaultFilterState.patchStatus).map(key => ({
    id: `patchStatus-${key}`,
    label: PATCH_STATUS_LABELS[key],
    dimensionLabel: `${PATCH_STATUS_LABELS[key]} patch status`,
    onRemove: () => filterState.togglePatchStatus(key),
  })),
]

export type ActiveFilterChipsPureProps = {
  chips: ActiveFilterChip[]
}

/**
 * A readout of what the table includes, not a clearing surface: there is no "Clear all" here. Each
 * chip's own remove button and the Filter dropdown's reset already clear filters.
 */
export const ActiveFilterChipsPure: React.FC<ActiveFilterChipsPureProps> = ({ chips }) => {
  if (chips.length === 0) {
    return null
  }

  return (
    <div className={styles.container}>
      {chips.map(chip => (
        <span key={chip.id} className={styles.chip}>
          <strong className={styles.chipLabel}>{chip.label}</strong>
          <button
            type="button"
            className={styles.removeButton}
            onClick={chip.onRemove}
            aria-label={`Remove ${chip.dimensionLabel} filter`}
          >
            <XIcon size={12} weight="bold" />
          </button>
        </span>
      ))}
    </div>
  )
}
